import type { ReactNode } from 'react';
import ServerStats from '../ServerStats';
import { CardTitle, ServerContainer } from '../styles';
import DraggableCard from '@/components/DraggableCard';
import CpuStats from '../CpuStats';
import Card from '@/components/Card';
import { MemoryStick } from 'lucide-react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import PieArcChart from '@/components/PieArcChart';
import DiskStats from '../DiskStats';
import { formatBytes } from '@/utils/formatters';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import type { DragStackState } from '../../../hooks/useDragReorder';

interface ServerInfoProps {
  dashboard: DashboardResponse;
  editMode: boolean;
  dragStack: DragStackState;
  userPreferences: UserPreferencesType;
}

function ServerInfo({
  dashboard,
  editMode,
  dragStack,
  userPreferences,
}: ServerInfoProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  const {
    order,
    draggingIndex,
    dragOverIndex,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
  } = dragStack;

  const totalMemory = dashboard?.serverInfo.memory.total || 0;
  const freeMemory = dashboard?.serverInfo.memory.free || 0;
  const memoryUsed = totalMemory - freeMemory;
  const memoryUnit = formatBytes(totalMemory).split(' ')[1];
  const memoryPercent = Math.round((memoryUsed / totalMemory) * 100);

  const memoryPieChartData = [
    {
      value: Number(formatBytes(memoryUsed, { base: 1024 }).split(' ')[0]),
      color: 'url(#pieChartGradient)',
      label: t('equipment.used'),
    },
    {
      value: Number(
        formatBytes(totalMemory - memoryUsed, { base: 1024 }).split(' ')[0]
      ),
      color: theme.palette.grey['05'],
      label: t('equipment.free'),
    },
  ];

  const serverCards: Record<string, ReactNode> = {
    serverData: (
      <ServerStats
        serverIPv4={dashboard?.serverIPv4}
        metrics={dashboard?.serverInfo}
      />
    ),
    cpuData: <CpuStats metrics={dashboard?.serverInfo} />,
    memoryData: (
      <Card>
        <CardTitle>
          <MemoryStick size="18px" />
          <Typography variant="h4">{t('dashboard.memory')}</Typography>
        </CardTitle>
        <Stack width="100%" direction="row" justifyContent="center" gap={2}>
          <PieArcChart
            data={memoryPieChartData || []}
            percentage={memoryPercent || 0}
            unit={memoryUnit}
            legendPosition="bottom"
          />
        </Stack>
      </Card>
    ),
    diskData: <DiskStats disks={dashboard?.serverInfo.disk} />,
  };

  return (
    <ServerContainer>
      {order.map(
        (id, index) =>
          userPreferences[id as keyof UserPreferencesType] !== false && (
            <DraggableCard
              key={id}
              editMode={editMode}
              isDragging={draggingIndex === index}
              isDragOver={dragOverIndex === index}
              onDragStart={handleDragStart(index)}
              onDragOver={handleDragOver(index)}
              onDrop={handleDrop(index)}
              onDragEnd={handleDragEnd}
            >
              {serverCards[id]}
            </DraggableCard>
          )
      )}
    </ServerContainer>
  );
}

export default ServerInfo;
